// #1665 — known birthdays in the Kin → Apple feed, as yearly all-day events.
//
// Kids come from family_context (category kid, key birthdate), Birthday
// Reminder contacts from the one scheduled_tasks row of type birthday_reminder
// (config.contacts[]). Each becomes ONE VEVENT with RRULE:FREQ=YEARLY, its
// DTSTART on the next occurrence on or after today, and a stable UID — so
// Apple shows it every year and never duplicates it on a re-pull.
//
// Month+day only, like the app's Today band (birthdayMatch.isBirthdayOnDate):
// a Feb 29 birthday therefore starts on the next leap year and Apple's yearly
// rule does what it does with Feb 29 from there.
//
// Pure: vitest imports it directly (src/lib/__tests__/birthdayFeedRows.test.ts).

import type { EventRow } from "./icsBuilder.ts";

export interface KidBirthdateFact {
  id: string;
  subject: string | null;
  value: string; // "YYYY-MM-DD" (the app writes full dates; "MM-DD" tolerated)
}

export interface BirthdayContact {
  name: string;
  birthday: string; // "YYYY-MM-DD" or "MM-DD"
}

const BIRTHDAY_RE = /^(?:(\d{4})-)?(\d{2})-(\d{2})$/;
const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Tolerant reader of scheduled_tasks.config.contacts — jsonb enforces nothing. */
export function contactsFromConfig(config: unknown): BirthdayContact[] {
  const raw = (config as { contacts?: unknown } | null | undefined)?.contacts;
  if (!Array.isArray(raw)) return [];
  const out: BirthdayContact[] = [];
  for (const c of raw) {
    if (!c || typeof c !== "object") continue;
    const name = String((c as { name?: unknown }).name ?? "").trim();
    const birthday = String((c as { birthday?: unknown }).birthday ?? "").trim();
    if (!name || !BIRTHDAY_RE.test(birthday)) continue;
    out.push({ name, birthday });
  }
  return out;
}

function isLeap(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}

/** Next "YYYY-MM-DD" on or after today with this month/day (Feb 29 → next leap year). */
export function nextBirthdayOnOrAfter(birthday: string, todayYmd: string): string | null {
  const b = BIRTHDAY_RE.exec(birthday);
  const t = YMD_RE.exec(todayYmd);
  if (!b || !t) return null;
  const [, , mm, dd] = b;
  const m = parseInt(mm, 10), d = parseInt(dd, 10);
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  // "02-30" / "04-31" pass the range check and would become an impossible
  // DTSTART (Copilot on #1669): a real calendar must agree the day exists.
  const probeYear = m === 2 && d === 29 ? 2024 : 2025;
  if (new Date(Date.UTC(probeYear, m - 1, d)).getUTCDate() !== d) return null;
  let year = parseInt(t[1], 10);
  for (let i = 0; i < 8; i++) {
    if (m === 2 && d === 29 && !isLeap(year)) { year++; continue; }
    const candidate = `${year}-${mm}-${dd}`;
    if (candidate >= todayYmd) return candidate;
    year++;
  }
  return null;
}

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "someone";
}

export function birthdayFeedRows(
  kids: KidBirthdateFact[] | null | undefined,
  contacts: BirthdayContact[] | null | undefined,
  todayYmd: string,
): EventRow[] {
  const rows: EventRow[] = [];
  for (const k of kids ?? []) {
    const value = String(k?.value ?? "").trim();
    const next = nextBirthdayOnOrAfter(value, todayYmd);
    if (!k || !next) continue;
    const name = (k.subject || "").trim() || "Someone in your crew";
    const yearMatch = BIRTHDAY_RE.exec(value);
    const birthYear = yearMatch && yearMatch[1] ? parseInt(yearMatch[1], 10) : null;
    const age = birthYear !== null ? parseInt(next.slice(0, 4), 10) - birthYear : null;
    rows.push({
      id: `birthday-kid-${k.id}`,
      title: `${name}'s birthday 🎂${age !== null && age >= 0 ? ` · turns ${age}` : ""}`,
      event_date: next,
      start_time: null,
      is_all_day: true,
      location: null,
      notes: "From Kin's Family Memory",
      rrule: "FREQ=YEARLY",
    });
  }
  for (const c of contacts ?? []) {
    const next = nextBirthdayOnOrAfter(String(c?.birthday ?? "").trim(), todayYmd);
    if (!c || !next) continue;
    rows.push({
      id: `birthday-contact-${slug(c.name)}-${next.slice(5)}`,
      title: `${c.name.trim()}'s birthday 🎂`,
      event_date: next,
      start_time: null,
      is_all_day: true,
      location: null,
      notes: "From your Birthday Reminders in Kin",
      rrule: "FREQ=YEARLY",
    });
  }
  return rows;
}
